// utils/aggregateRepeatedEvents.ts

import { GameLog } from "../../../../Logs/LogsDisplay";
import { DamageType } from "./utils";



export function aggregateRepeatedEvents(events: GameLog[]): GameLog[] {
  const aggregatedEventsMap: { [key: string]: GameLog } = {};
  const resourceAggregatedEventsMap: { [key: string]: GameLog } = {};
  const ccAggregatedEventsMap: { [key: string]: GameLog } = {};
  const result: GameLog[] = [];

  // Define DoT DamageTypes
  const dotDamageTypes = [DamageType.SpellPersistent, DamageType.PersistentAOE, DamageType.DefaultPeriodic];

  // Define Abilities to Aggregate
  const abilitiesToAggregate = new Set<string>([
    // Add unique ability names here
    // Akshan Swing
    // ... other abilities
  ]);

  for (const event of events) {
    const isDotDamageType = dotDamageTypes.includes(event.details.damageType);
    const isAggregatableAbility = abilitiesToAggregate.has(event.details.source);

    // Debug logging for resource events
    if (event.type === 'StatChange' && event.details.modifierType === 'ResourceUsage') {
      console.log('[DEBUG] Found resource usage event:', {
        type: event.type,
        modifierType: event.details.modifierType,
        statName: event.details.statName,
        changeAmount: event.details.changeAmount,
        source: event.details.source,
        championName: event.details.championName
      });
    }

    // Handle damage aggregation
    if (event.type === 'DamageTaken' && (isDotDamageType || isAggregatableAbility)) {
      const key = `${event.sequenceId}-${event.details.source}-${event.details.targetId || ''}`;

      if (aggregatedEventsMap[key]) {
        // Update cumulative damage and tick count
        aggregatedEventsMap[key].details.cumulativeDamage += event.details.damageAmount || 0;
        aggregatedEventsMap[key].details.tickCount += 1;
      } else {
        // Initialize aggregation
        aggregatedEventsMap[key] = {
          ...event,
          type: 'AggregatedDamageTaken',
          details: {
            ...event.details,
            cumulativeDamage: event.details.damageAmount || 0,
            tickCount: 1,
          },
          childEvents: [],
        };
        result.push(aggregatedEventsMap[key]);
      }
    }
    // Handle resource aggregation
    else if (event.type === 'StatChange' && event.details.modifierType === 'ResourceUsage') {
      const resourceKey = `${event.sequenceId}-${event.details.source}-${event.details.championName}-resource`;
      
      console.log('[DEBUG] Processing resource aggregation:', {
        resourceKey,
        exists: !!resourceAggregatedEventsMap[resourceKey],
        changeAmount: event.details.changeAmount
      });

      if (resourceAggregatedEventsMap[resourceKey]) {
        // Update cumulative resource cost and tick count
        resourceAggregatedEventsMap[resourceKey].details.cumulativeResourceCost += Math.abs(event.details.changeAmount || 0);
        resourceAggregatedEventsMap[resourceKey].details.tickCount += 1;
        // Ensure maxResourceAmount is preserved (use the first event's value if current event doesn't have it)
        if (!resourceAggregatedEventsMap[resourceKey].details.maxResourceAmount && event.details.maxResourceAmount) {
          resourceAggregatedEventsMap[resourceKey].details.maxResourceAmount = event.details.maxResourceAmount;
        }
        console.log('[DEBUG] Updated existing aggregation:', {
          newTickCount: resourceAggregatedEventsMap[resourceKey].details.tickCount,
          newCumulative: resourceAggregatedEventsMap[resourceKey].details.cumulativeResourceCost,
          maxResourceAmount: resourceAggregatedEventsMap[resourceKey].details.maxResourceAmount
        });
      } else {
        // Determine actual resource type (backend uses 'currentResource' as statName)
        // We need to determine the actual resource type from context or default to 'mana'
        const actualResourceType = 'mana'; // Default for now, could be improved with champion data
        
        // Initialize resource aggregation
        const aggregatedEvent = {
          ...event,
          type: 'AggregatedResourceUsage',
          details: {
            ...event.details,
            cumulativeResourceCost: Math.abs(event.details.changeAmount || 0),
            individualResourceCost: Math.abs(event.details.changeAmount || 0),
            tickCount: 1,
            // Use determined resource type instead of statName
            resourceType: actualResourceType,
            // Store max resource amount for accurate percentage calculation
            maxResourceAmount: event.details.maxResourceAmount || event.details.totalAmount || 100,
            totalResourceAmount: event.details.totalAmount || 100,
          },
          childEvents: [],
        };
        
        console.log('[DEBUG] Created new aggregated resource event:', aggregatedEvent);
        resourceAggregatedEventsMap[resourceKey] = aggregatedEvent;
        result.push(aggregatedEvent);
      }
    }
    // Handle crowd control aggregation
    else if (event.type === 'CrowdControl') {
      // Create key based on ccType, duration, and strength to group identical CC effects
      const ccKey = `${event.sequenceId}-${event.details.ccType}-${event.details.duration}-${event.details.strength || 'none'}-${event.details.source}-${event.details.targetChampionName}`;
      
      console.log('[DEBUG] Processing CC aggregation:', {
        ccKey,
        exists: !!ccAggregatedEventsMap[ccKey],
        ccType: event.details.ccType,
        duration: event.details.duration,
        strength: event.details.strength
      });

      if (ccAggregatedEventsMap[ccKey]) {
        // Update tick count for repeated identical CC effects
        ccAggregatedEventsMap[ccKey].details.tickCount += 1;
        console.log('[DEBUG] Updated existing CC aggregation:', {
          newTickCount: ccAggregatedEventsMap[ccKey].details.tickCount,
          ccType: event.details.ccType
        });
      } else {
        // Initialize CC aggregation
        const aggregatedCCEvent = {
          ...event,
          type: 'AggregatedCrowdControl',
          details: {
            ...event.details,
            tickCount: 1,
          },
          childEvents: [],
        };
        
        console.log('[DEBUG] Created new aggregated CC event:', aggregatedCCEvent);
        ccAggregatedEventsMap[ccKey] = aggregatedCCEvent;
        result.push(aggregatedCCEvent);
      }
    } else {
      // Include non-aggregated events
      result.push(event);
    }
  }

  return result;
}
